import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { type ReactNode } from "react";

export interface SubsidizedPayLabelProps {
  originalRate: string;
  subsidizedRate: string;
  size?: "small" | "medium";
  totalLabel?: ReactNode;
}

const PILL_HORIZONTAL_PADDING = 2;

/**
 * Displays the original (pre-subsidy) pay rate with grey strikethrough
 * alongside the new boosted rate in bold green.
 * When `totalLabel` is provided it renders below the boosted rate pill.
 */
export function SubsidizedPayLabel(props: SubsidizedPayLabelProps) {
  const { originalRate, subsidizedRate, size = "medium", totalLabel } = props;
  const theme = useTheme();

  const originalVariant = size === "small" ? "caption" : "body2";
  const subsidizedVariant = size === "small" ? "body2" : "body1";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "auto auto",
        columnGap: 2,
        rowGap: 1,
        alignItems: "center",
      }}
    >
      <Text
        variant={originalVariant}
        sx={{
          textDecoration: "line-through",
          color: theme.palette.text.tertiary,
        }}
      >
        {originalRate}
      </Text>
      <Text
        bold
        variant={subsidizedVariant}
        sx={{
          color: theme.palette.intent?.success.text,
          backgroundColor: theme.palette.intent?.success.background,
          paddingX: PILL_HORIZONTAL_PADDING,
          paddingY: 0.5,
          borderRadius: 3,
        }}
      >
        {subsidizedRate} /hr
      </Text>
      {isDefined(totalLabel) && (
        <>
          <Box />
          <Box sx={{ paddingLeft: PILL_HORIZONTAL_PADDING }}>{totalLabel}</Box>
        </>
      )}
    </Box>
  );
}
